import { ICompanyModel } from '../models';

import { addMonths, setMonthDateWithoutOverflow, subtractMonths } from '../../shared/helpers/abacusTimeFormatter';

import { AMEX_BILLING_STRIPE_PLAN_TYPE } from '../constants';

export function checkIfBillingPlanIsAmex(billingPlan: string) {
  return ([AMEX_BILLING_STRIPE_PLAN_TYPE.FREE_PLAN, AMEX_BILLING_STRIPE_PLAN_TYPE.PRO_PLAN] as string[]).includes(billingPlan);
}

export function getLatestAndNextBillingDates(company: ICompanyModel) {
  let latestBillingDate: Date, nextBillingDate: Date;

  const currentMonth = new Date().getMonth();
  const prevMonth = subtractMonths(new Date(), 1).getMonth();
  const nextMonth = addMonths(new Date(), 1).getMonth();
  const billingDay = company.billing_day;

  if (new Date().getDate() > billingDay) {
    latestBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), currentMonth), billingDay);
    nextBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), nextMonth), billingDay);
  } else {
    latestBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), prevMonth), billingDay);
    nextBillingDate = setMonthDateWithoutOverflow(new Date(new Date().getFullYear(), currentMonth), billingDay);
  }

  return { latestBillingDate, nextBillingDate };
}

interface MileageRateEntry {
  startDate: string;
  rate: number;
}

export function getMileageRateFromConfig(mileage_rate_per_year: ICompanyModel['mileage_rate_per_year'], expenseDate?: number) {
  if (mileage_rate_per_year.hasOwnProperty('custom')) {
    return mileage_rate_per_year.custom;
  }

  const date = new Date(expenseDate ?? Date.now());
  const rates: MileageRateEntry[] = mileage_rate_per_year;

  const matchingRate = rates.filter((entry) => new Date(entry.startDate) <= date).pop();

  return matchingRate ? matchingRate.rate : rates[0].rate;
}

/**
 * Maps currency codes to their distance unit and standard mileage rate type.
 * Only currencies with a known standard rate are listed; all others default to miles.
 */
export const STANDARD_RATE_BY_CURRENCY: Record<string, { unit: 'miles' | 'km'; rateType: 'irs' | 'ato' }> = {
  USD: { unit: 'miles', rateType: 'irs' },
  AUD: { unit: 'km', rateType: 'ato' }
};

/**
 * Returns the distance unit for the given currency code.
 * Defaults to 'miles' for unknown currencies.
 */
export function getDistanceUnitForCurrency(currencyCode: string): 'miles' | 'km' {
  return STANDARD_RATE_BY_CURRENCY[currencyCode]?.unit ?? 'miles';
}

/**
 * Returns the standard mileage rate type for the given currency code,
 * or null if no standard rate applies.
 */
export function getStandardRateType(currencyCode: string): 'irs' | 'ato' | null {
  return STANDARD_RATE_BY_CURRENCY[currencyCode]?.rateType ?? null;
}

/**
 * Returns a lower-case readable label for a distance unit.
 */
export function getDistanceUnitLowerLabel(distanceUnit: string): 'mile' | 'kilometer' {
  return distanceUnit === 'km' ? 'kilometer' : 'mile';
}

interface IStandardMileageDisplaySettings {
  standardRateType: 'irs' | 'ato';
  distanceUnit: 'miles' | 'km';
  distanceUnitLabel: 'mile' | 'km';
  standardRateLabel: string;
  standardMileageRate?: number;
  customRatePrefix: string;
}

type MileageRateFields = {
  currency_code?: string;
  irs_mileage_rate?: number;
  ato_mileage_rate?: number;
};

/**
 * Returns currency-aware mileage display settings for admin configuration UIs.
 */
export function getStandardMileageDisplaySettings(orgData: MileageRateFields): IStandardMileageDisplaySettings {
  const companyCurrencyCode = orgData.currency_code || 'USD';
  const standardRateType = getStandardRateType(companyCurrencyCode) || 'irs';
  const distanceUnit = getDistanceUnitForCurrency(companyCurrencyCode);
  const windowObject = (global as { window?: { irs_mileage_rate?: number; ato_mileage_rate?: number } }).window;

  return {
    standardRateType,
    distanceUnit,
    distanceUnitLabel: distanceUnit === 'km' ? 'km' : 'mile',
    standardRateLabel: standardRateType.toUpperCase(),
    standardMileageRate: standardRateType === 'ato' ? windowObject?.ato_mileage_rate : windowObject?.irs_mileage_rate,
    customRatePrefix: companyCurrencyCode === 'AUD' ? 'A$ 0.' : '$ 0.'
  };
}

/**
 * The company address fields that must be populated for a company to be
 * considered to have completed its onboarding info.
 */
export const COMPANY_INFO_REQUIRED_FIELDS = ['postal_code', 'street_address', 'address_state'] as const;

type CompanyInfoInput = Pick<ICompanyModel, 'postal_code' | 'street_address' | 'address_state'>;

/**
 * Returns the required company onboarding info fields that are missing (empty,
 * null, or undefined) on the given company.
 *
 * @param company - the company (model instance or serialized object) to check
 */
export function getMissingCompanyInfoFields(company: CompanyInfoInput): typeof COMPANY_INFO_REQUIRED_FIELDS[number][] {
  return COMPANY_INFO_REQUIRED_FIELDS.filter((field) => !company[field]);
}

/**
 * Returns whether the given company has every required onboarding info field
 * populated. Extracted from the company model's `has_info_filled` virtual so
 * callers can compute this directly instead of relying on the serialized virtual.
 *
 * @param company - the company (model instance or serialized object) to check
 */
export function companyHasInfoFilled(company: CompanyInfoInput): boolean {
  return getMissingCompanyInfoFields(company).length === 0;
}
